import { ReactNode, useState } from 'react'
import { X, Loader2 } from 'lucide-react'

interface AnnouncementBannerProps {
  icon: ReactNode
  title: string
  description: string
  actionLabel?: string
  onAction?: () => void
  actionLoading?: boolean
  dismissible?: boolean
  secondaryActionLabel?: string
  onSecondaryAction?: () => void
  secondaryActionLoading?: boolean
}

const AnnouncementBanner = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  actionLoading = false,
  dismissible = true,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionLoading = false
}: AnnouncementBannerProps) => {
  // Solo en memoria: recargar, cambiar de wallet o cerrar sesión lo vuelve a mostrar.
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) return null

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation()
    setDismissed(true)
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full bg-card border-2 border-border-base rounded-2xl px-4 py-3.5 transition-opacity duration-200 ease-in-out">
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <div className="w-11 h-11 shrink-0 rounded-xl primary-gradient flex items-center justify-center">
          {icon}
        </div>

        <div className="flex flex-col min-w-0 flex-1">
          <h3 className="font-bold text-text-main">{title}</h3>
          <p className="text-sm text-text-secondary">{description}</p>
        </div>

        {dismissible && (
          <button
            onClick={handleDismiss}
            className="sm:hidden shrink-0 p-1 rounded-full text-text-secondary hover:text-text-main transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {(actionLabel || secondaryActionLabel || dismissible) && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0 justify-end">
          {secondaryActionLabel && onSecondaryAction && (
            <button
              onClick={onSecondaryAction}
              disabled={secondaryActionLoading}
              className="rounded-full py-1.5 px-4 text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-60 w-full sm:w-auto border-2 border-border-base text-text-main hover:bg-card-hover transition-colors"
            >
              {secondaryActionLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {secondaryActionLabel}
            </button>
          )}
          {actionLabel && onAction && (
            <button
              onClick={onAction}
              disabled={actionLoading}
              className="btn-gradient-fill rounded-full py-1.5 px-4 text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-60 w-full sm:w-auto"
            >
              {actionLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {actionLabel}
            </button>
          )}
          {dismissible && (
            <button
              onClick={handleDismiss}
              className="hidden sm:block shrink-0 p-1 rounded-full text-text-secondary hover:text-text-main transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default AnnouncementBanner
